import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
} from "@/components/ui/combobox";
import { InputGroupAddon } from "@/components/ui/input-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AppColorVariant, colorVariantClasses } from "@/theme/AppColorVariant";

export interface FilterComboboxOption {
  value: string;
  label: React.ReactNode;
  color?: AppColorVariant;
  icon?: React.ElementType;
}

interface FilterComboboxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: FilterComboboxOption[];
  placeholder: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  searchable?: boolean;
}

export function FilterCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  className,
  triggerClassName,
  searchable = false,
}: FilterComboboxProps) {
  const selected = options.find((opt) => opt.value === value) ?? null;
  const SelectedIcon = selected?.icon;
  const selectedIconColor = selected?.color
    ? colorVariantClasses[selected.color]?.text
    : undefined;

  // Disclose truncated selected values via tooltip. We detect truncation on
  // the rendered <input> inside the InputGroup rather than measuring the
  // label string, so the check stays accurate across font loading, zoom,
  // container resizes, and icon addons that reduce available width.
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const tooltipLabel =
    typeof selected?.label === "string" ? selected.label : null;

  useEffect(() => {
    const input = triggerRef.current?.querySelector<HTMLInputElement>("input");
    if (!input) {
      setIsTruncated(false);
      return;
    }
    const check = () => {
      setIsTruncated(input.scrollWidth > input.clientWidth + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(input);
    return () => ro.disconnect();
  }, [tooltipLabel, value]);

  const trigger = (
    <div ref={triggerRef} className="w-full">
      <ComboboxInput
        placeholder={placeholder}
        readOnly={!searchable}
        showClear={!!value && !disabled}
        className={cn("w-full", className, triggerClassName)}
      >
        {SelectedIcon && (
          <InputGroupAddon align="inline-start">
            <SelectedIcon className={cn("size-4 shrink-0", selectedIconColor)} />
          </InputGroupAddon>
        )}
      </ComboboxInput>
    </div>
  );

  return (
    <Combobox
      items={options}
      value={selected}
      onValueChange={(opt) => onChange(opt?.value ?? null)}
      isItemEqualToValue={(a, b) => a.value === b.value}
      itemToStringValue={(opt) => String(opt.label)}
      disabled={disabled}
    >
      {isTruncated && tooltipLabel ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent sideOffset={4}>{tooltipLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <ComboboxContent>
        <ComboboxEmpty>No results.</ComboboxEmpty>
        <ComboboxList>
          {(opt: FilterComboboxOption) => {
            const iconColorClass = opt.color
              ? colorVariantClasses[opt.color]?.text
              : undefined;
            const Icon = opt.icon;
            return (
              <ComboboxItem key={opt.value} value={opt}>
                {Icon && <Icon className={cn("size-4 shrink-0", iconColorClass)} />}
                <span>{opt.label}</span>
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
